<template>
  <v-container>
    <h2>Showcase gaps audit</h2>
    <p class="subtitle">
      One row per dancer. Sortable by tightest change-time gap so the close calls bubble to the top.
      Durations use each piece's actual music length when uploaded
      (3:15 fallback for solos missing music, 4 min fallback for corps without uploaded music).
      Transitions assume 30 sec between solos, 1 min before/after corps. Intermission is 15 min.
    </p>

    <p v-if="loading" class="muted">Loading…</p>

    <div v-else>
      <div class="controls">
        <label>
          Sort:
          <select v-model="sortBy">
            <option value="tightest">Tightest gap first</option>
            <option value="name">Dancer name</option>
            <option value="appearances">Most appearances first</option>
          </select>
        </label>
        <label class="threshold">
          Flag gaps under:
          <input type="number" min="0" v-model.number="flagMin" /> min
        </label>
      </div>

      <p class="muted">{{ dancers.length }} dancers</p>

      <div class="cards">
        <div
          v-for="row in sortedDancers"
          :key="row.name"
          class="dancer-card"
          :class="{ 'is-flagged': row.minGapMin != null && row.minGapMin < flagMin }"
        >
          <div class="card-header">
            <div class="dancer-name">{{ row.name }}</div>
            <div class="card-stats">
              <span class="stat-label">{{ row.appearances.length }} appearance{{ row.appearances.length === 1 ? '' : 's' }}</span>
              <span v-if="row.minGapMin != null" class="stat-divider">·</span>
              <span v-if="row.minGapMin != null" class="stat-label">
                Tightest gap
                <span class="stat-value" :class="{ 'gap-flag': row.minGapMin < flagMin }">≈ {{ row.minGapMin }} min</span>
              </span>
            </div>
          </div>
          <ol class="timeline">
            <template v-for="(item, idx) in row.timeline">
              <li v-if="item.kind === 'appearance'" :key="'a' + idx" class="tl-appearance">
                <span class="tl-marker">●</span>
                <span class="tl-label">{{ item.label }}</span>
              </li>
              <li v-else-if="item.kind === 'intermission'" :key="'i' + idx" class="tl-intermission">
                <span class="tl-rule"></span>
                <span class="tl-intermission-pill">— 15-min Intermission —</span>
                <span class="tl-rule"></span>
              </li>
              <li v-else :key="'g' + idx" class="tl-gap" :class="{ 'gap-flag': item.minutes < flagMin }">
                <span class="tl-rule"></span>
                <span class="tl-gap-text">↓ ≈ {{ item.minutes }} min</span>
                <span class="tl-rule"></span>
              </li>
            </template>
          </ol>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { db } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'


const SOLO_FALLBACK_SEC = 60 * 3 + 15
const TRANSITION_SEC = 30
const BLOCK_INTRO_SEC = 30
const CORPS_FALLBACK_SEC = 4 * 60
const CORPS_TRANSITION_SEC = 60
const INTERMISSION_SEC = 15 * 60

const SHOWCASE_STRUCTURE = [
  { type: 'soloBlock', block: 'AA', name: 'Solo Block AA' },
  { type: 'corps', name: 'Alice in Wonderland' },
  { type: 'soloBlock', block: 'A', name: 'Solo Block A' },
  { type: 'corps', name: 'Becoming' },
  { type: 'soloBlock', block: 'B', name: 'Solo Block B' },
  { type: 'intermission', name: 'Intermission' },
  { type: 'corps', name: 'Waltz of the Hours' },
  { type: 'soloBlock', block: 'C', name: 'Solo Block C' },
  { type: 'corps', name: 'Le Jazz Hot' },
  { type: 'soloBlock', block: 'D', name: 'Solo Block D' },
  { type: 'corps', name: 'Garland Waltz' }
]

function interleave (a, b) {
  const out = []
  const max = Math.max(a.length, b.length)
  for (let i = 0; i < max; i++) {
    if (i < a.length) out.push(a[i])
    if (i < b.length) out.push(b[i])
  }
  return out
}

function autoOrder (pieces) {
  // Comp/non-comp alternation carries across placement-group boundaries.
  const groups = { beginning: [], middle: [], end: [], unspecified: [] }
  for (const p of pieces) {
    const k = p.showcasePlacement && groups[p.showcasePlacement] !== undefined ? p.showcasePlacement : 'unspecified'
    groups[k].push(p)
  }
  const unspecHalf = Math.ceil(groups.unspecified.length / 2)
  const groupSequence = [
    groups.beginning,
    groups.unspecified.slice(0, unspecHalf),
    groups.middle,
    groups.unspecified.slice(unspecHalf),
    groups.end
  ]
  const isComp = p => !!(p.competition && p.competition !== 'None')
  const out = []
  let lastType = null
  for (const list of groupSequence) {
    const comp = list.filter(isComp)
    const non = list.filter(p => !isComp(p))
    if (!comp.length && !non.length) continue
    let first, second
    if (!comp.length) { first = non; second = [] }
    else if (!non.length) { first = comp; second = [] }
    else if (lastType === 'comp') { first = non; second = comp }
    else if (lastType === 'non') { first = comp; second = non }
    else {
      first = comp.length >= non.length ? comp : non
      second = first === comp ? non : comp
    }
    const interleaved = interleave(first, second)
    out.push(...interleaved)
    if (interleaved.length) {
      lastType = isComp(interleaved[interleaved.length - 1]) ? 'comp' : 'non'
    }
  }
  return out
}

function orderBlockPieces (pieces) {
  if (!pieces.length) return []
  const pinnedNumeric = pieces
    .filter(p => Number.isFinite(p.showcaseOrder) && p.showcaseOrder >= 1)
    .slice()
    .sort((a, b) => a.showcaseOrder - b.showcaseOrder)
  const pinnedLast = pieces.filter(p => p.showcaseOrder === 'last')
  const unpinned = pieces.filter(p => !pinnedNumeric.includes(p) && !pinnedLast.includes(p))
  const N = pieces.length
  const slots = new Array(N).fill(null)
  for (const p of pinnedNumeric) {
    let target = Math.max(1, Math.min(p.showcaseOrder, N)) - 1
    while (target < N && slots[target] !== null) target += 1
    if (target >= N) target = slots.findIndex(s => s === null)
    if (target !== -1) slots[target] = p
  }
  for (const p of pinnedLast) {
    let target = N - 1
    while (target >= 0 && slots[target] !== null) target -= 1
    if (target === -1) target = slots.findIndex(s => s === null)
    if (target !== -1) slots[target] = p
  }
  const isComp = p => !!(p.competition && p.competition !== 'None')
  const groups = { beginning: [], middle: [], end: [], unspecified: [] }
  for (const p of unpinned) {
    const k = p.showcasePlacement && groups[p.showcasePlacement] !== undefined ? p.showcasePlacement : 'unspecified'
    groups[k].push(p)
  }
  const half = Math.ceil(groups.unspecified.length / 2)
  const placementOrder = [
    ...groups.beginning,
    ...groups.unspecified.slice(0, half),
    ...groups.middle,
    ...groups.unspecified.slice(half),
    ...groups.end
  ]
  const compQueue = placementOrder.filter(isComp)
  const nonQueue = placementOrder.filter(p => !isComp(p))
  for (let i = 0; i < N; i++) {
    if (slots[i] !== null) continue
    let prevType = null
    for (let j = i - 1; j >= 0; j--) {
      if (slots[j] !== null) { prevType = isComp(slots[j]) ? 'comp' : 'non'; break }
    }
    let nextType = null
    if (prevType === null) {
      for (let j = i + 1; j < N; j++) {
        if (slots[j] !== null) { nextType = isComp(slots[j]) ? 'comp' : 'non'; break }
      }
    }
    let wantComp
    if (prevType === 'comp') wantComp = false
    else if (prevType === 'non') wantComp = true
    else if (nextType === 'comp') wantComp = false
    else if (nextType === 'non') wantComp = true
    else wantComp = compQueue.length >= nonQueue.length
    if (wantComp && compQueue.length) slots[i] = compQueue.shift()
    else if (!wantComp && nonQueue.length) slots[i] = nonQueue.shift()
    else if (compQueue.length) slots[i] = compQueue.shift()
    else if (nonQueue.length) slots[i] = nonQueue.shift()
  }
  return slots
}

function splitDancers (str) {
  if (!str) return []
  return String(str).split(/[,&]| and /i).map(s => s.trim()).filter(Boolean)
}

function pieceDancerNames (piece) {
  return [...splitDancers(piece.programDancer), ...splitDancers(piece.namedDancers)]
}

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      loading: true,
      pieces: [],
      corpsCasts: {},
      corpsMusic: {},
      sortBy: 'tightest',
      flagMin: 5
    }
  },
  async created () {
    const piecesSnap = await db.collection(this.IABF.pieces).get()
    this.pieces = piecesSnap.docs.map(d => ({ id: d.id, ...d.data() }))
    const configDoc = await db.collection(this.IABF.config).doc('showcase-runtime').get()
    const data = configDoc.exists ? (configDoc.data() || {}) : {}
    this.corpsCasts = data.corpsCasts || {}
    this.corpsMusic = data.corpsMusic || {}
    this.loading = false
  },
  computed: {
    dancerNames () {
      const seen = new Map()
      const add = (raw) => {
        const name = String(raw || '').trim()
        if (!name) return
        const key = name.toLowerCase()
        if (!seen.has(key)) seen.set(key, name)
      }
      for (const p of this.pieces) {
        if (!p.showcaseBlock) continue
        for (const n of pieceDancerNames(p)) add(n)
      }
      for (const corpsName of Object.keys(this.corpsCasts)) {
        for (const group of (this.corpsCasts[corpsName] || [])) {
          for (const d of (group.dancers || [])) add(d)
        }
      }
      // Drop single-token names (e.g. "Shawn") when exactly one multi-token
      // name in the candidate list starts with that token (e.g. "Shawn
      // Ondo"). Substring matching would already attribute every "Shawn"
      // appearance to "Shawn Ondo", so the short version is a duplicate.
      const all = Array.from(seen.values())
      const fullByFirst = new Map()
      for (const n of all) {
        const tokens = n.split(/\s+/)
        if (tokens.length > 1) {
          const ft = tokens[0].toLowerCase()
          if (!fullByFirst.has(ft)) fullByFirst.set(ft, [])
          fullByFirst.get(ft).push(n)
        }
      }
      return all.filter(n => {
        const tokens = n.split(/\s+/)
        if (tokens.length > 1) return true
        const matches = fullByFirst.get(tokens[0].toLowerCase()) || []
        return matches.length !== 1
      })
    },
    dancers () {
      return this.dancerNames
        .map(name => this.buildRow(name))
        .filter(r => r.appearances.length > 0)
    },
    sortedDancers () {
      const list = this.dancers.slice()
      if (this.sortBy === 'name') {
        list.sort((a, b) => a.name.localeCompare(b.name))
      } else if (this.sortBy === 'appearances') {
        list.sort((a, b) => b.appearances.length - a.appearances.length || a.name.localeCompare(b.name))
      } else {
        list.sort((a, b) => {
          const av = a.minGapMin == null ? Infinity : a.minGapMin
          const bv = b.minGapMin == null ? Infinity : b.minGapMin
          return av - bv || a.name.localeCompare(b.name)
        })
      }
      return list
    }
  },
  methods: {
    matchesDancer (haystack, dancerName) {
      const dn = dancerName.toLowerCase()
      return String(haystack || '').toLowerCase().includes(dn)
    },
    pieceMatchesDancer (piece, dancerName) {
      return this.matchesDancer(piece.programDancer, dancerName) ||
             this.matchesDancer(piece.namedDancers, dancerName)
    },
    corpsMatchesDancer (corpsName, dancerName) {
      const groups = this.corpsCasts[corpsName] || []
      for (const group of groups) {
        for (const d of (group.dancers || [])) {
          if (this.matchesDancer(d, dancerName)) return true
        }
      }
      return false
    },
    soloSeconds (piece) {
      const n = parseInt(piece && piece.lengthInSeconds, 10)
      return Number.isFinite(n) && n > 0 ? n : SOLO_FALLBACK_SEC
    },
    corpsSeconds (corpsName) {
      const entry = this.corpsMusic[corpsName]
      if (!entry) return CORPS_FALLBACK_SEC
      const tracks = Array.isArray(entry.tracks)
        ? entry.tracks
        : (entry.url ? [{ lengthInSeconds: entry.lengthInSeconds }] : [])
      let total = 0
      for (const t of tracks) {
        const n = parseInt(t && t.lengthInSeconds, 10)
        if (Number.isFinite(n) && n > 0) total += n
      }
      return total > 0 ? total : CORPS_FALLBACK_SEC
    },
    buildRow (dancerName) {
      const appearances = []
      let t = 0
      for (const slot of SHOWCASE_STRUCTURE) {
        if (slot.type === 'soloBlock') {
          const all = orderBlockPieces(this.pieces.filter(p => p.showcaseBlock === slot.block))
          t += BLOCK_INTRO_SEC
          all.forEach((piece, idx) => {
            const soloSec = this.soloSeconds(piece)
            const start = t
            const end = t + soloSec
            t += soloSec + TRANSITION_SEC
            if (this.pieceMatchesDancer(piece, dancerName)) {
              const title = piece.programTitle || piece.title || '(untitled)'
              appearances.push({
                kind: 'appearance',
                label: `${slot.block}${idx + 1}: ${title}`,
                startSec: start,
                endSec: end,
                isIntermissionAdjacent: false
              })
            }
          })
        } else if (slot.type === 'corps') {
          const corpsSec = this.corpsSeconds(slot.name)
          const start = t
          t += corpsSec
          const end = t
          t += CORPS_TRANSITION_SEC
          if (this.corpsMatchesDancer(slot.name, dancerName)) {
            appearances.push({
              kind: 'appearance',
              label: slot.name,
              startSec: start,
              endSec: end
            })
          }
        } else if (slot.type === 'intermission') {
          appearances.push({ kind: 'intermission', startSec: t, endSec: t + INTERMISSION_SEC })
          t += INTERMISSION_SEC
        }
      }
      // Strip leading/trailing intermission markers so they only sit between
      // genuine appearances.
      while (appearances.length && appearances[0].kind === 'intermission') appearances.shift()
      while (appearances.length && appearances[appearances.length - 1].kind === 'intermission') appearances.pop()

      const realAppearances = appearances.filter(a => a.kind === 'appearance')
      const timeline = []
      let prev = null
      for (let i = 0; i < appearances.length; i++) {
        const item = appearances[i]
        if (item.kind === 'appearance') {
          if (prev) {
            const gapSec = Math.max(0, item.startSec - prev.endSec)
            timeline.push({ kind: 'gap', minutes: Math.round(gapSec / 60) })
          }
          timeline.push(item)
          prev = item
        } else {
          timeline.push(item)
          // Don't reset prev — gap calc continues across intermission, but
          // we render the intermission marker in between for readability.
        }
      }

      // Compute tightest non-intermission-only gap between consecutive
      // appearances. The intermission's 15 min is counted in those gaps
      // automatically because we never add it back after stripping.
      let minGapMin = null
      for (let i = 1; i < realAppearances.length; i++) {
        const gapSec = Math.max(0, realAppearances[i].startSec - realAppearances[i - 1].endSec)
        const gapMin = Math.round(gapSec / 60)
        if (minGapMin == null || gapMin < minGapMin) minGapMin = gapMin
      }

      return {
        name: dancerName,
        appearances: realAppearances,
        timeline,
        minGapMin
      }
    }
  }
}
</script>

<style scoped>
h2 {
  font-family: Quattrocento, Georgia, serif;
  font-size: 32px;
  margin-bottom: 8px;
}

.subtitle {
  font-family: 'Fanwood Text', Georgia, serif;
  color: #555;
  margin-bottom: 16px;
  max-width: 720px;
}

.muted {
  color: #888;
  font-style: italic;
}

.controls {
  display: flex;
  gap: 24px;
  align-items: center;
  margin-bottom: 12px;
  font-family: 'Fanwood Text', Georgia, serif;
}

.controls select,
.controls input {
  font: inherit;
  padding: 2px 6px;
  margin-left: 6px;
}

.controls .threshold input {
  width: 60px;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
}

.dancer-card {
  background: #fff;
  border: 1px solid #e6e1d4;
  border-radius: 8px;
  padding: 14px 16px 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.dancer-card.is-flagged {
  border-color: #e8b4a8;
  background: #fff8f6;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.dancer-name {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 700;
  font-size: 17px;
  color: #1a1a1a;
}

.card-stats {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 13px;
  color: #777;
}

.stat-divider {
  margin: 0 6px;
  color: #ccc;
}

.stat-value {
  font-weight: 700;
  color: #555;
  margin-left: 4px;
}

.stat-value.gap-flag {
  color: #c0392b;
}

.timeline {
  list-style: none;
  padding: 0;
  margin: 0;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
}

.tl-appearance {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #f9f7f1;
  border-radius: 6px;
  color: #1a1a1a;
}

.tl-marker {
  color: #267E7D;
  font-size: 10px;
  line-height: 1;
}

.tl-label {
  font-weight: 600;
}

.tl-gap,
.tl-intermission {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  color: #888;
  font-style: italic;
  font-size: 13px;
}

.tl-gap.gap-flag {
  color: #c0392b;
  font-weight: 700;
}

.tl-intermission {
  color: #267E7D;
  font-style: normal;
  font-weight: 600;
}

.tl-rule {
  flex: 1;
  height: 1px;
  background: currentColor;
  opacity: 0.25;
}

.tl-gap-text,
.tl-intermission-pill {
  white-space: nowrap;
}
</style>
</content>
</invoke>